import { Badge, Box } from '@mantine/core';
import clsx from 'clsx';
import classes from './PlayingCard.module.css';

export type PlayingCardProps = {
  /** Card face artwork. Always at the printed 7:12 proportion. */
  frontImage: string | null;
  /** Deck back. Falls back to a labelled placeholder when a deck has none. */
  backImage?: string | null;
  /** `false` shows the back — the default state of a card in play (§36). */
  faceUp?: boolean;
  label?: string;
  /** Small badge in the corner — position number, sector, participant. */
  caption?: string;
  onClick?: () => void;
  width?: number | string;
  disabled?: boolean;
};

/**
 * The single card primitive (§5.1 — a domain component, not a UI-kit clone).
 *
 * The image is the content; everything else is chrome. Used by spreads, the
 * game board, deck previews and the design-system page alike.
 */
export function PlayingCard({
  frontImage,
  backImage,
  faceUp = true,
  label,
  caption,
  onClick,
  width = 140,
  disabled,
}: PlayingCardProps) {
  const interactive = Boolean(onClick) && !disabled;

  return (
    <Box w={width} pos="relative">
      {caption ? (
        <Badge className={classes.caption} size="sm" variant="filled" color="teal">
          {caption}
        </Badge>
      ) : null}

      <button
        type="button"
        className={classes.root}
        onClick={onClick}
        disabled={!interactive}
        aria-label={label ?? (faceUp ? 'Карта лицом вверх' : 'Карта рубашкой вверх')}
      >
        <div className={clsx(classes.inner, !faceUp && classes.flipped)}>
          <div className={classes.face}>
            {frontImage ? (
              <img className={classes.image} src={frontImage} alt={label ?? 'Карта'} />
            ) : (
              <div className={classes.placeholder}>Изображение карты</div>
            )}
          </div>

          <div className={clsx(classes.face, classes.back)}>
            {backImage ? (
              <img className={classes.image} src={backImage} alt="Рубашка карты" />
            ) : (
              <div className={classes.placeholder}>Рубашка</div>
            )}
          </div>
        </div>
      </button>
    </Box>
  );
}
